import { useCallback } from 'react';
import { coursesApiService } from '../course/services/api';
import { Course } from '../../interfaces/course.interface';
import { Module } from '../../interfaces/module.interface';
import { CalendarEvent } from '../../interfaces/event.interface';
import { Report } from '../../interfaces/report.interface';

export interface HomeCourse {
  course: Course;
  modules: Module[];
  views: string[];
  events: CalendarEvent[];
  report: Report | null;
}

const safe = async <T,>(promise: Promise<T>, fallback: T): Promise<T> => {
  try {
    return await promise;
  } catch {
    return fallback;
  }
};

export function useHomeCore() {
  /** Avance, próximas sesiones y último reporte de cada curso. Un curso que falla no bota el Inicio. */
  const getHomeCourses = useCallback(
    async (courses: Course[], onSuccess?: (data: HomeCourse[]) => void, onFailure?: () => void) => {
      try {
        const data = await Promise.all(
          courses.map(async (course) => {
            const [modules, views, events, report] = await Promise.all([
              safe(coursesApiService.getModules(course.id).then((r) => r.modules), [] as Module[]),
              safe(coursesApiService.getCourseViews(course.id).then((r) => r.views), [] as string[]),
              course.calendar ? safe(coursesApiService.getCalendar(course.id).then((r) => r.events), [] as CalendarEvent[]) : Promise.resolve([] as CalendarEvent[]),
              course.report ? safe(coursesApiService.getReport(course.id).then((r) => r.report ?? null), null as Report | null) : Promise.resolve(null),
            ]);
            return { course, modules, views, events, report };
          }),
        );
        onSuccess?.(data);
      } catch (error) {
        console.error(error);
        onFailure?.();
      }
    },
    [],
  );

  /** Miniatura del video de la clase (oEmbed público de Vimeo, el mismo que usa el reproductor). */
  const getVideoThumbnail = useCallback(async (videoUrl: string): Promise<string | null> => {
    try {
      // Las clases guardan solo el id de Vimeo; oEmbed necesita la URL completa
      const url = /^\d+$/.test(videoUrl) ? `https://vimeo.com/${videoUrl}` : videoUrl;
      const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}&width=960`);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data.thumbnail_url || null;
    } catch {
      return null;
    }
  }, []);

  return { getHomeCourses, getVideoThumbnail };
}
